import {
  ChangeDetectionStrategy,
  Component,
  inject,
  TemplateRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import {
  concatMap,
  filter,
  iif,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { PROFILE_MENU_ITEMS } from '../../../profile/profile.constants';
import { UserInformationService } from '../../../core/services/user/user-information.service';
import { environment } from '../../../../environments/environment';
import { HEADER_MENU_ITEMS_NATIVE, HEADER_MENU_ITEMS_WEB } from './header-menu.const';
import { IUserAccount } from '../../services/user-accounts/model/user-account.interface';
import {
  ButtonComponent,
  DeviceDetectionService,
  DropdownTriggerDirective,
  IconButtonComponent,
  MenuComponent,
  MenuItem,
  SvgIconComponent,
} from '@ui-lib/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth/auth.service';
import { AsyncPipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { UserAccountsComponent } from '../user-accounts/user-accounts.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { MobileMenuComponent } from '../mobile-menu/mobile-menu.component';
import { LanguageService } from '../../../core/services/language/language.service';
import { Capacitor } from '@capacitor/core';

const HEADER_MENU_ITEMS = Capacitor.isNativePlatform()
  ? HEADER_MENU_ITEMS_NATIVE
  : HEADER_MENU_ITEMS_WEB;

@Component({
  selector: 'rrp-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    AsyncPipe,
    IconButtonComponent,
    DropdownTriggerDirective,
    RouterLink,
    NgOptimizedImage,
    ButtonComponent,
    TranslateModule,
    SvgIconComponent,
    UserAccountsComponent,
    MenuComponent,
    RouterLinkActive,
    NgTemplateOutlet,
    LanguageSelectorComponent,
    MobileMenuComponent,
  ],
})
export class HeaderComponent implements AfterViewInit {
  public readonly deviceDetectionService = inject(DeviceDetectionService);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly translateService = inject(TranslateService);
  private readonly userInformationService = inject(UserInformationService);

  @ViewChild('accountMenu')
  private readonly accountMenu?: TemplateRef<unknown>;

  private readonly subscriptionDestroy$: Subject<boolean> =
    new Subject<boolean>();

  public readonly registrationUrl: string = environment.registrationUrl;

  public readonly userInfo$ = this.userInformationService.userInfo$;

  public readonly currentLanguage$: Observable<string> =
    this.languageService.getCurentLanguageObservable();

  public readonly userAccount$: Observable<IUserAccount | null | undefined> =
    this.userInfo$.pipe(
      concatMap((userInfo) =>
        iif(
          () => !!userInfo,
          this.userInformationService.getCurrentAccount(),
          of(null),
        ),
      ),
    );

  public readonly isLoading$ = this.userInfo$.pipe(
    map((userInfo) => userInfo === undefined),
  );

  public readonly topMenuItems$: Observable<MenuItem[]> = this.userInfo$.pipe(
    map((userInfo) => !!userInfo),
    map((authorized: boolean) =>
      HEADER_MENU_ITEMS.filter(
        (item: MenuItem) =>
          (authorized && item.isAuthorizeOnly) || !item.isAuthorizeOnly,
      ),
    ),
  );

  public profileMenuItems: MenuItem[] = [];

  public languageMenuItems: MenuItem[] = [];

  public get adminPanelUrl(): string {
    return environment.adminPanelUrl;
  }

  public ngAfterViewInit(): void {
    this.profileMenuItems = [
      {
        titleStream$: this.userAccount$.pipe(
          switchMap((account) => {
            if (!account) {
              return this.translateService.stream(
                'header.buttons.all-accounts',
              );
            }

            return of(account.label);
          }),
        ),
        icon: 'users',
        dropdownMenu: this.accountMenu,
        isMobileOnly: true,
      },
      ...PROFILE_MENU_ITEMS,
      {
        titleTranslateKey: 'sidebar-menu.logout',
        icon: 'logout',
        callback: (): void => this.logout(),
        isSecondaryStyle: true,
      },
    ];

    const adminPanelLink: MenuItem = {
      titleTranslateKey: 'header.buttons.admin-panel',
      icon: 'user',
      isSecondaryStyle: true,
      isMobileOnly: true,
      callback: (): void => {
        this.openAdminPanel();
      },
    };

    this.userInfo$
      .pipe(
        takeUntil(this.subscriptionDestroy$),
        filter((userInfo) => !!userInfo?.isAdmin),
      )
      .subscribe(() => {
        if (!this.profileMenuItems.includes(adminPanelLink)) {
          this.profileMenuItems.unshift(adminPanelLink);
        }
      });
  }

  public registration(): void {
    const currentLanguage = this.languageService.getCurentLanguage();

    window.location.href = `${environment.registrationUrl}?lang=${currentLanguage}`;
  }

  public openAdminPanel(): void {
    const currentLanguage = this.languageService.getCurentLanguage();

    window.location.href = `${this.adminPanelUrl}?lang=${currentLanguage}`;
  }

  public logout(): void {
    void this.authService.logout();
  }
}
